import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save } from "lucide-react";

interface Sport {
  id: string;
  name: string;
  icon: string | null;
  is_active: boolean;
}

const AdminSports = () => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadSports();
  }, []);

  const loadSports = async () => {
    const { data } = await supabase.from("sports").select("*").order("name");
    if (data) setSports(data as Sport[]);
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    const { error } = await supabase.from("sports").insert({ name: newName.trim() });
    if (error) toast.error("Failed to add sport");
    else {
      toast.success("Sport added");
      setNewName("");
      loadSports();
    }
    setAdding(false);
  };

  const toggleActive = async (sport: Sport) => {
    await supabase
      .from("sports")
      .update({ is_active: !sport.is_active })
      .eq("id", sport.id);
    setSports((prev) =>
      prev.map((s) => (s.id === sport.id ? { ...s, is_active: !s.is_active } : s))
    );
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this sport? This may affect existing data.")) return;
    await supabase.from("sports").delete().eq("id", id);
    setSports((prev) => prev.filter((s) => s.id !== id));
    toast.success("Sport deleted");
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="font-display text-3xl font-bold">Sports</h1>
          <p className="text-muted-foreground mt-1">
            Manage the list of sports available to athletes.
          </p>
        </div>

        {/* Add new */}
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New sport name..."
            className="flex-1 h-10 rounded-lg border border-border/50 bg-secondary/30 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          />
          <Button onClick={handleAdd} disabled={adding || !newName.trim()}>
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Add
          </Button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {sports.map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/30"
            >
              <div className="flex items-center gap-3">
                <span className="font-medium text-sm">{s.name}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    s.is_active
                      ? "bg-green-500/10 text-green-500"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {s.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => toggleActive(s)}>
                  {s.is_active ? "Deactivate" : "Activate"}
                </Button>
                <button
                  onClick={() => handleDelete(s.id)}
                  className="text-muted-foreground hover:text-destructive p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSports;
